<script setup lang="ts">
import {
  formatDateRangeLong,
  getPeriodRange,
  periodPresetOptions,
  type PeriodPresetKey,
} from '@/utils/format';

const { t } = useI18n();
const route = useRoute();
const { companyId, from, to, periodPreset, applySnapshot } = useFinanceContext();

const companies = ref<Array<{ id: number; code: string; name: string }>>([]);
const open = ref(true);
const BAR_KEY = 'jgi.financeBarOpen';

/** Draft filter — diterapkan lewat tombol Terapkan */
const draftCompanyId = ref<number | null>(null);
const draftFrom = ref('');
const draftTo = ref('');
const draftPreset = ref<PeriodPresetKey>('this_month');
const applyingPreset = ref(false);

const periodOptions = computed(() => periodPresetOptions(new Date(), t));

/**
 * Hanya tampil di modul yang memang memakai filter PT + periode global.
 * Halaman lain (kalender, HR, pajak, klien, dll.) punya filter sendiri —
 * jangan tampilkan bar ini di semua route.
 */
const FINANCE_BAR_PREFIXES = [
  '/journals',
  '/quick-journal',
  '/opening-balance',
  '/statements',
  '/reconciliation',
  '/cash-bank',
  '/bank-accounts',
  '/accounts',
  '/reports',
  '/downloads',
  '/import',
  '/audit',
  '/control/close-checklist',
  '/fixed-assets',
  '/recurring-journals',
  '/invoices',
  '/vendor-bills',
  '/hr/employees',
  '/hr/attendance',
  '/tax',
] as const;

const { setTarget, loadCompanies: loadPtCompanies } = usePtTarget();

const showBar = computed(() => {
  const p = String(route.path || '');
  if (!p || p.startsWith('/login') || p.startsWith('/auth')) return false;
  if (p === '/') return true;
  return FINANCE_BAR_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
});

const activeCode = computed(
  () => companies.value.find((c) => c.id === companyId.value)?.code || null
);
const draftCode = computed(
  () => companies.value.find((c) => c.id === draftCompanyId.value)?.code || null
);
const periodTitle = computed(() => {
  if (draftPreset.value === 'custom') return t('period.custom');
  return periodOptions.value.find((o) => o.value === draftPreset.value)?.title || draftPreset.value;
});
const rangeChip = computed(() => formatDateRangeLong(draftFrom.value, draftTo.value));
const dirty = computed(() => {
  return (
    draftCompanyId.value !== companyId.value ||
    draftFrom.value !== from.value ||
    draftTo.value !== to.value ||
    draftPreset.value !== periodPreset.value
  );
});

function pullFromContext() {
  draftCompanyId.value = companyId.value;
  draftFrom.value = from.value;
  draftTo.value = to.value;
  draftPreset.value = periodPreset.value;
}

function setPt(id: number | null) {
  draftCompanyId.value = id;
}

function pickPeriod(key: PeriodPresetKey) {
  if (!key || key === 'custom') {
    draftPreset.value = 'custom';
    return;
  }
  applyingPreset.value = true;
  const range = getPeriodRange(key);
  draftFrom.value = range.from;
  draftTo.value = range.to;
  draftPreset.value = key;
  nextTick(() => {
    applyingPreset.value = false;
  });
}

function onDraftDates() {
  if (applyingPreset.value) return;
  draftPreset.value = 'custom';
}

function syncPtTargetFromCompany(id: number | null) {
  if (id == null) {
    setTarget(null);
    return;
  }
  const code = String(companies.value.find((c) => c.id === id)?.code || '')
    .trim()
    .toUpperCase();
  if (code === 'JGI' || code === 'JGT') setTarget(code);
  else setTarget(null);
}

function applyFilters() {
  applySnapshot({
    companyId: draftCompanyId.value,
    from: draftFrom.value,
    to: draftTo.value,
    periodPreset: draftPreset.value,
  });
  syncPtTargetFromCompany(draftCompanyId.value);
}

function resetFilters() {
  setPt(null);
  pickPeriod('this_month');
  nextTick(() => applyFilters());
}

function toggle() {
  open.value = !open.value;
  if (import.meta.client) localStorage.setItem(BAR_KEY, open.value ? '1' : '0');
}

/** Sinkron draft bila konteks diubah dari halaman lain */
watch([companyId, from, to, periodPreset], () => pullFromContext());

onMounted(async () => {
  if (import.meta.client) open.value = localStorage.getItem(BAR_KEY) !== '0';
  try {
    companies.value = await $fetch('/api/companies');
    await loadPtCompanies(companies.value);
  } catch {
    companies.value = [];
  }
  pullFromContext();
  syncPtTargetFromCompany(companyId.value);
});

watch(companyId, (id) => syncPtTargetFromCompany(id));
</script>

<template>
  <section v-if="showBar" class="fcb" :class="{ 'is-collapsed': !open }">
    <div class="fcb__head">
      <button type="button" class="fcb__head-main" @click="toggle">
        <span class="fcb__icon" aria-hidden="true">
          <v-icon size="22">mdi-filter-variant</v-icon>
        </span>
        <span class="fcb__copy">
          <span class="fcb__kicker">{{ t('fcb.kicker') }}</span>
          <strong class="fcb__title">{{ t('fcb.title') }}</strong>
          <em class="fcb__sub">{{ t('fcb.sub') }}</em>
        </span>
      </button>

      <div class="fcb__head-right">
        <div class="fcb-pt fcb-pt--head" role="group" :aria-label="t('dash.filterPt')">
          <button
            type="button"
            class="fcb-pt__btn"
            :class="{ 'fcb-pt__btn--on': !draftCompanyId }"
            @click="setPt(null)"
          >
            {{ t('dash.allPt') }}
          </button>
          <button
            v-for="c in companies"
            :key="`h-${c.id}`"
            type="button"
            class="fcb-pt__btn"
            :class="{ 'fcb-pt__btn--on': draftCompanyId === c.id }"
            @click="setPt(c.id)"
          >
            {{ c.code }}
          </button>
        </div>
        <span v-if="!open" class="fcb__range-chip">
          {{ activeCode || t('dash.allPt') }} · {{ formatDateRangeLong(from, to) }}
        </span>
        <button
          type="button"
          class="fcb__toggle"
          :class="{ 'fcb__toggle--on': open }"
          @click="toggle"
        >
          <span>{{ open ? t('dash.fxClose') : t('dash.fxOpen') }}</span>
          <v-icon size="18">{{ open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </button>
      </div>
    </div>

    <v-expand-transition>
      <div v-show="open" class="fcb__body">
        <div class="fcb-filters">
          <div class="fcb-filters__field fcb-filters__field--ptseg">
            <span class="fcb-filters__label">{{ t('dash.filterPt') }}</span>
            <div class="fcb-pt" role="group">
              <button
                type="button"
                class="fcb-pt__btn"
                :class="{ 'fcb-pt__btn--on': !draftCompanyId }"
                @click="setPt(null)"
              >
                {{ t('dash.allPt') }}
              </button>
              <button
                v-for="c in companies"
                :key="c.id"
                type="button"
                class="fcb-pt__btn"
                :class="{ 'fcb-pt__btn--on': draftCompanyId === c.id }"
                @click="setPt(c.id)"
              >
                {{ c.code }}
              </button>
            </div>
          </div>

          <div class="fcb-filters__field fcb-filters__field--pt">
            <span class="fcb-filters__label">{{ t('dash.fxSearchPt') }}</span>
            <v-autocomplete
              v-model="draftCompanyId"
              :items="companies"
              item-title="name"
              item-value="id"
              :placeholder="t('dash.allPt')"
              clearable
              density="compact"
              hide-details
              variant="outlined"
              class="fcb-field"
              prepend-inner-icon="mdi-magnify"
            />
          </div>

          <div class="fcb-filters__field fcb-filters__field--period">
            <span class="fcb-filters__label">{{ t('dash.period') }}</span>
            <v-select
              :model-value="draftPreset"
              :items="periodOptions"
              item-title="title"
              item-value="value"
              density="compact"
              hide-details
              variant="outlined"
              class="fcb-field fcb-period"
              :class="{
                'fcb-period--bulan': draftPreset === 'this_month',
                'fcb-period--tahun': draftPreset === 'this_year',
              }"
              @update:model-value="pickPeriod"
            >
              <template #selection="{ item }">
                <span class="fcb-period__sel">
                  <span
                    v-if="draftPreset === 'this_month' || draftPreset === 'this_year'"
                    class="fcb-period__dot"
                    :class="{ 'fcb-period__dot--blue': draftPreset === 'this_year' }"
                    aria-hidden="true"
                  />
                  {{ item.title }}
                </span>
              </template>
            </v-select>
          </div>

          <div class="fcb-filters__dates">
            <div class="fcb-filters__field fcb-filters__field--date">
              <span class="fcb-filters__label">{{ t('dash.from') }}</span>
              <AccountingDateDmyField
                v-model="draftFrom"
                class="fcb-field fcb-field--date"
                @update:model-value="onDraftDates"
              />
            </div>
            <div class="fcb-filters__field fcb-filters__field--date">
              <span class="fcb-filters__label">{{ t('dash.to') }}</span>
              <AccountingDateDmyField
                v-model="draftTo"
                class="fcb-field fcb-field--date"
                @update:model-value="onDraftDates"
              />
            </div>
          </div>

          <div class="fcb-filters__actions">
            <button type="button" class="fcb-filters__reset" @click="resetFilters">
              <v-icon size="16">mdi-restore</v-icon>
              {{ t('common.reset') }}
            </button>
            <button
              type="button"
              class="fcb-filters__apply"
              :class="{ 'is-dirty': dirty }"
              @click="applyFilters"
            >
              <v-icon size="16">mdi-check-bold</v-icon>
              {{ t('dash.fxApply') }}
            </button>
          </div>
        </div>

        <div class="fcb-filters__meta">
          <span class="fcb-filters__pill">
            <v-icon size="14">mdi-office-building-outline</v-icon>
            {{ draftCode || t('dash.allPt') }}
          </span>
          <span class="fcb-filters__pill">
            <v-icon size="14">mdi-calendar-range</v-icon>
            {{ rangeChip }}
          </span>
          <span class="fcb-filters__pill fcb-filters__pill--period">
            <v-icon size="14">mdi-clock-outline</v-icon>
            {{ periodTitle }}
          </span>
          <span v-if="dirty" class="fcb-filters__pill fcb-filters__pill--dirty">
            {{ t('fcb.dirty') }}
          </span>
        </div>
      </div>
    </v-expand-transition>
  </section>
</template>

<style scoped>
.fcb {
  position: relative;
  margin: 0 0 14px;
  border-radius: 18px;
  border: 1px solid rgba(191, 219, 254, 0.85);
  background:
    radial-gradient(ellipse 70% 90% at 0% 0%, rgba(59, 130, 246, 0.14), transparent 55%),
    radial-gradient(ellipse 55% 70% at 100% 0%, rgba(20, 184, 166, 0.12), transparent 50%),
    linear-gradient(180deg, #f8fbff 0%, #ffffff 55%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 14px 32px -22px rgba(37, 99, 235, 0.45);
  overflow: hidden;
}
.fcb::before {
  content: '';
  position: absolute;
  inset: 0 0 auto 0;
  height: 3px;
  background: linear-gradient(90deg, #2563eb, #0ea5e9 45%, #14b8a6);
}
.fcb__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  padding: 0.85rem 1.05rem;
}
.fcb__head-main {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
  padding: 0;
}
.fcb__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: #fff;
  background: linear-gradient(145deg, #3b9dff 0%, #2ec4b6 100%);
  box-shadow: 0 8px 18px -8px rgba(59, 157, 255, 0.7);
}
.fcb__copy {
  min-width: 0;
}
.fcb__kicker {
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #64748b;
}
.fcb__title {
  display: block;
  margin: 0.1rem 0 0.15rem;
  font-size: 1.02rem;
  font-weight: 850;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.25;
}
.fcb__sub {
  display: block;
  font-style: normal;
  font-size: 0.78rem;
  color: #64748b;
  line-height: 1.35;
  max-width: 36rem;
}
.fcb__head-right {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-shrink: 0;
}
.fcb-pt {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 999px;
  background: #eef2f7;
  border: 1px solid #e2e8f0;
}
.fcb-pt__btn {
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.32rem 0.7rem;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.fcb-pt__btn:hover {
  color: #0f172a;
}
.fcb-pt__btn--on {
  background: #0f172a;
  color: #fff;
  box-shadow: 0 4px 10px -4px rgba(15, 23, 42, 0.55);
}
.fcb__range-chip {
  font-size: 0.7rem;
  font-weight: 700;
  color: #475569;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 0.28rem 0.65rem;
  white-space: nowrap;
}
.fcb__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: 0;
  border-radius: 999px;
  padding: 0.45rem 0.95rem;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%);
  box-shadow: 0 8px 18px -10px rgba(37, 99, 235, 0.85);
}
.fcb__toggle--on {
  background: linear-gradient(135deg, #0f172a 0%, #334155 100%);
  box-shadow: 0 8px 18px -12px rgba(15, 23, 42, 0.75);
}
.fcb__body {
  padding: 0 1.05rem 1.05rem;
}
.fcb-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.65rem 0.75rem;
  padding: 0.75rem 0.85rem;
  margin-bottom: 0.55rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(226, 232, 240, 0.95);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
}
.fcb-filters__field {
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
  min-width: 0;
}
.fcb-filters__field--ptseg {
  flex: 0 1 auto;
}
.fcb-filters__field--pt {
  flex: 1.1 1 160px;
}
.fcb-filters__field--period {
  flex: 0.9 1 150px;
}
.fcb-filters__dates {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  flex: 0 1 auto;
  min-width: min(100%, 312px);
}
.fcb-filters__field--date {
  flex: 0 0 152px;
  width: 152px;
  min-width: 152px;
  max-width: 100%;
}
.fcb-filters__label {
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.fcb-filters__actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: auto;
  padding-bottom: 1px;
}
.fcb-filters__reset,
.fcb-filters__apply {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: 0;
  border-radius: 10px;
  padding: 0.42rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 750;
  cursor: pointer;
}
.fcb-filters__reset {
  background: #f1f5f9;
  color: #475569;
}
.fcb-filters__reset:hover {
  background: #e2e8f0;
}
.fcb-filters__apply {
  color: #fff;
  background: linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%);
  box-shadow: 0 8px 16px -10px rgba(37, 99, 235, 0.85);
}
.fcb-filters__apply.is-dirty {
  animation: fcb-pulse 1.4s ease-in-out infinite;
}
.fcb-filters__apply:hover {
  filter: brightness(1.05);
}
.fcb-filters__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.fcb-filters__pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
}
.fcb-filters__pill--period {
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #bfdbfe;
}
.fcb-filters__pill--dirty {
  color: #b45309;
  background: #fffbeb;
  border-color: #fde68a;
}
.fcb-field :deep(.v-field) {
  border-radius: 10px !important;
  font-size: 0.82rem;
}
.fcb-field :deep(.v-field__outline) {
  --v-field-border-opacity: 0.35;
}
.fcb-period__sel {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
.fcb-period__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
}
.fcb-period__dot--blue {
  background: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
}
@keyframes fcb-pulse {
  0%,
  100% {
    box-shadow: 0 8px 16px -10px rgba(37, 99, 235, 0.85);
  }
  50% {
    box-shadow: 0 10px 22px -8px rgba(37, 99, 235, 1);
  }
}
@media (max-width: 900px) {
  .fcb-pt--head {
    display: none;
  }
  .fcb__head {
    flex-wrap: wrap;
  }
  .fcb-filters__actions {
    margin-left: 0;
    width: 100%;
  }
}
@media (max-width: 640px) {
  .fcb__sub {
    display: none;
  }
}
</style>
